import { useMemo, useState } from 'react';
import { Badge, Checkbox, Input } from '@/shared/components';
import { useAddOnsQuery } from '../hooks/useAddOnsQuery';
import type { AddOnRecord } from '../services/smr-admin';

interface Props {
    value: string[];
    onChange: (ids: string[]) => void;
    /**
     * Add-ons already attached to the group being edited. May include
     * retired add-ons that the active-only `useAddOnsQuery` source omits.
     * Without this, retired pre-existing members would silently disappear
     * from the edit picker and become impossible to remove without a direct
     * API call. See PR #318 review.
     */
    existingMembers?: AddOnRecord[];
}

export function AddOnGroupMemberSelect({ value, onChange, existingMembers = [] }: Props) {
    const [search, setSearch] = useState('');
    const query = useAddOnsQuery({ status: 'active', search: search || undefined, per_page: 50 });
    const queryAddons = query.data?.data;

    // Union active add-ons with any existing (possibly retired) members so
    // historic memberships stay visible and removable in edit mode. Dedupe by
    // id; preserve the active list as the leading order.
    const addons = useMemo<AddOnRecord[]>(() => {
        const activeAddons: AddOnRecord[] = queryAddons ?? [];
        if (existingMembers.length === 0) {
            return activeAddons;
        }
        const seen = new Set(activeAddons.map((a) => a.id));
        const extras: AddOnRecord[] = [];
        for (const member of existingMembers) {
            if (!seen.has(member.id)) {
                extras.push(member);
                seen.add(member.id);
            }
        }
        // If a search term is set, also filter the existing-only extras by
        // name so they don't ignore the user's filter input.
        const term = search.trim().toLowerCase();
        const filteredExtras = term
            ? extras.filter((a) => a.name.toLowerCase().includes(term))
            : extras;
        return [...activeAddons, ...filteredExtras];
    }, [queryAddons, existingMembers, search]);

    const toggle = (id: string) => {
        onChange(value.includes(id) ? value.filter((v) => v !== id) : [...value, id]);
    };

    return (
        <div className="space-y-2">
            <Input placeholder="Search optional extras..." value={search} onChange={(e) => setSearch(e.target.value)} />
            {query.isFetching && <p className="text-xs text-mp-muted">Loading...</p>}
            <div className="max-h-48 space-y-1 overflow-y-auto rounded border border-mp-border p-2">
                {addons.length === 0 && !query.isFetching && (
                    <p className="text-xs text-mp-muted">No active optional extras found.</p>
                )}
                {addons.map((addon) => {
                    const isRetired = !!addon.retired_at;
                    return (
                        <div key={addon.id}>
                            <Checkbox
                                checked={value.includes(addon.id)}
                                onChange={() => toggle(addon.id)}
                                labelClassName="flex items-center gap-2 text-sm"
                                label={
                                    <>
                                        <span className={isRetired ? 'font-medium text-mp-muted line-through' : 'font-medium'}>
                                            {addon.name}
                                        </span>
                                        {addon.category && <span className="text-mp-muted">({addon.category})</span>}
                                        {isRetired && <Badge variant="warning">retired</Badge>}
                                    </>
                                }
                            />
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
